<template>
  <!-- Public, unauthenticated SOS page. Two modes:
       - /sos/<slug>  : tenant driver form - drop a pin, fill in vehicle/driver, send an SOS
                        to that company's dispatch (internal only; staff escalate to the
                        network separately from inside the app).
       - /sos         : open, tenant-less INFO-ONLY page - drop a pin, pick a radius, and see
                        nearby bus yards on the map with contact info (nothing is sent). -->
  <div class="sos-form-bg" :class="{ 'sos-form-bg--fixed': publicMode }">
    <div class="sos-form-card" :class="{ 'sos-form-card--fill': publicMode }">

      <header class="sos-form-header" :class="{ 'sos-form-header--slim': publicMode, 'sos-form-header--oneline': !publicMode }">
        <h1 v-if="publicMode" class="sos-form-oneline">
          <span class="sos-form-brand">Bus SOS</span>
          <span class="sos-form-oneline-sub">- Public Yard Map</span>
        </h1>
        <h1 v-else class="sos-form-oneline">
          <span class="sos-form-brand">Bus SOS</span>
          <span class="sos-form-oneline-sub" v-if="statusMode">- Request Status</span>
          <template v-else>
            <span class="sos-form-oneline-sub">- Request Help</span>
            <span class="sos-form-oneline-co" v-if="display_name">{{ display_name }}</span>
          </template>
        </h1>
      </header>

      <div v-if="loading" class="sos-form-state"><b-spinner small></b-spinner>&nbsp;Loading&hellip;</div>
      <div v-else-if="loadError" class="sos-form-state sos-form-state--error">
        This SOS link isn't valid. Check the URL or contact your dispatcher.
      </div>

      <!-- ===================== DRIVER STATUS (/sos/<slug>/<token>) ===================== -->
      <div v-else-if="statusMode" class="sos-status">
        <div v-if="statusLoading && !statusData" class="sos-form-state"><b-spinner small></b-spinner>&nbsp;Looking up your SOS&hellip;</div>
        <div v-else-if="statusError" class="sos-form-state sos-form-state--error">
          We couldn't find that SOS. Double-check the link, or contact your dispatcher.
        </div>
        <template v-else-if="statusData">
          <div class="sos-status-head">
            <span class="sos-status-chip" :class="'sos-status-chip--' + statusData.status">{{ statusLabel(statusData.status) }}</span>
          </div>

          <div v-if="statusData.notification_failed" class="sos-form-state sos-form-state--error">
            Dispatch alerts aren't set up for this company &mdash; <strong>call your dispatcher directly</strong> to be sure they know.
          </div>

          <div class="sos-status-facts">
            <div v-if="statusData.company"><span class="sos-status-k">Company</span><span class="sos-status-v">{{ statusData.company }}</span></div>
            <div v-if="statusData.dispatcher_phone"><span class="sos-status-k">Dispatch</span><span class="sos-status-v"><a :href="dispatchPhoneLink" class="sos-status-tel"><i class="fa fa-phone"></i>&nbsp;{{ dispatchPhone }}</a></span></div>
            <div v-if="statusData.escalation_phone"><span class="sos-status-k">Escalation</span><span class="sos-status-v"><a :href="escalationPhoneLink" class="sos-status-tel"><i class="fa fa-phone"></i>&nbsp;{{ escalationPhone }}</a></span></div>
            <div v-if="statusData.vehicle_name"><span class="sos-status-k">Vehicle</span><span class="sos-status-v">{{ statusData.vehicle_name }}</span></div>
            <div v-if="statusData.requested_time"><span class="sos-status-k">Requested</span><span class="sos-status-v">{{ fmtTime(statusData.requested_time) }}</span></div>
            <div v-if="statusData.address" class="sos-status-wide"><span class="sos-status-k">Location</span><span class="sos-status-v">{{ statusData.address }}</span></div>
          </div>

          <gmap-map v-if="statusCenter" :zoom="12" :center="statusCenter" :options="themed_map_options"
                    class="sos-form-map sos-form-map--compact">
            <gmap-marker :position="statusCenter" :icon="sourceIcon"/>
          </gmap-map>

          <!-- two-way notes between the driver and their dispatcher -->
          <div class="sos-list-head sos-list-head--info">
            <i class="fa fa-comments-o"></i>&nbsp;Comments
            <span class="sos-list-count">{{ orderedComments.length }}</span>
          </div>
          <div v-if="!orderedComments.length" class="sos-form-noresults">No comments yet. Add a note for your dispatcher below.</div>
          <div v-else class="sos-status-comments">
            <div v-for="(c, i) in orderedComments" :key="i" class="sos-status-comment" :class="'sos-status-comment--' + (c.role || 'note')">
              <div class="sos-status-comhead">
                <span class="sos-status-comwho">{{ commentWho(c) }}</span>
                <span class="sos-status-comtime" v-if="c.time">{{ fmtTime(c.time) }}</span>
              </div>
              <div class="sos-status-combody">{{ c.text }}</div>
            </div>
          </div>
          <div class="sos-status-comadd">
            <b-form-textarea v-model="commentText" rows="2" max-rows="4" size="sm" maxlength="500"
                             placeholder="Add a comment for your dispatcher…"/>
            <b-button size="sm" variant="primary" class="sos-status-comsend" :disabled="commentBusy || !commentText.trim()" @click="add_comment">
              <b-spinner v-if="commentBusy" small></b-spinner>
              <template v-else><i class="fa fa-paper-plane"></i>&nbsp;Add comment</template>
            </b-button>
          </div>

          <div class="sos-status-foot">
            <button type="button" class="sos-icon-btn" @click="load_status" :disabled="statusLoading" title="Refresh" aria-label="Refresh">
              <b-spinner v-if="statusLoading" small></b-spinner>
              <i v-else class="fa fa-refresh"></i>
            </button>
            <span class="sos-status-autonote">Updates automatically</span>
          </div>
        </template>
      </div>

      <!-- ===================== PUBLIC (info-only) ===================== -->
      <div v-else-if="publicMode" class="sos-public">
        <div class="sos-ctrl-row">
          <span class="sos-cluster-wrap" v-if="!pin_position">
            <span class="sos-cluster-lead" @click="clustering = !clustering">Cluster</span>
            <b-form-checkbox v-model="clustering" switch class="sos-cluster-toggle"
                             aria-label="Cluster" title="Group nearby yard pins into clusters"/>
          </span>
          <div class="sos-ctrl-radius" v-if="pin_position">
            <div class="sos-ctrl-label">Find Yards Within <strong>{{ radius }} miles</strong></div>
            <input type="range" min="5" max="150" step="5" v-model.number="radius" class="sos-form-slider"
                   @change="find_nearby" aria-label="Search radius in miles"/>
          </div>
          <div class="sos-ctrl-count" v-else>{{ yardCountLabel }}</div>
          <button type="button" class="sos-icon-btn" @click="locate_me(false)" :disabled="locating"
                  title="Use my location" aria-label="Use my location">
            <b-spinner v-if="locating" small></b-spinner>
            <i v-else class="fa fa-location-arrow"></i>
          </button>
        </div>

        <gmap-map ref="sosMap" :zoom="map_zoom" :center="map_center"
                  :options="themed_map_options"
                  class="sos-form-map sos-form-map--compact" :class="{ 'sos-form-map--pinned': pin_position }"
                  @click="place_pin_and_search">
          <gmap-circle v-if="pin_position" :center="pin_position" :radius="radiusMeters"
                       :options="{ strokeColor: '#c0392b', strokeOpacity: 0.5, strokeWeight: 1, fillColor: '#c0392b', fillOpacity: 0.07, clickable: false }"/>
          <gmap-marker v-if="pin_position" :position="pin_position" :draggable="true" @dragend="place_pin_and_search"/>
          <!-- yard pins: clustered by default (the directory is ~1,000 points nationwide), or every
               pin shown when the viewer turns clustering off. re-key so a toggle or mode switch
               rebuilds the markers/clusterer cleanly -->
          <gmap-cluster v-if="showClusters" :key="'c-' + (pin_position ? 'nearby' : 'all')" :zoom-on-click="true"
                        :grid-size="55" :max-zoom="12" :minimum-cluster-size="4" :styles="clusterStyles">
            <gmap-marker v-for="(y, i) in mapYards" :key="'y' + i" :position="{ lat: Number(y.lat), lng: Number(y.lon) }"
                         :icon="yardIcon" :label="pin_position ? markerLabel('Y' + (i + 1)) : null" :title="y.company" @click="focus_yard(y, true)"/>
          </gmap-cluster>
          <template v-else>
            <gmap-marker v-for="(y, i) in mapYards" :key="'ym' + i" :position="{ lat: Number(y.lat), lng: Number(y.lon) }"
                         :icon="yardIcon" :label="pin_position ? markerLabel('Y' + (i + 1)) : null" :title="y.company" @click="focus_yard(y, true)"/>
          </template>
          <gmap-info-window :position="info.position" :opened="info.open" :options="{ pixelOffset: { width: 0, height: -34 } }">
            <div v-html="info.html"></div>
          </gmap-info-window>
        </gmap-map>

        <div class="sos-form-pinline" v-if="pin_position"><i class="fa fa-map-marker"></i>&nbsp;{{ address || 'Location set on the map' }}<button type="button" class="sos-pin-clear" @click="clear_pin" title="Remove pin" aria-label="Remove pin"><i class="fa fa-times"></i>&nbsp;Remove pin</button></div>
        <div v-if="geo_error" class="sos-form-geoerr">{{ geo_error }}</div>
        <div v-else-if="!pin_position" class="sos-form-geoerr">Tap the map or use your location to search.</div>
        <div v-if="submit_error" class="sos-form-state sos-form-state--error">{{ submit_error }}</div>

        <!-- the nearby list only appears once a pin is dropped; before that the map shows every yard -->
        <template v-if="pin_position">
          <div class="sos-list-head sos-list-head--info">
            <i class="fa fa-map-marker"></i>&nbsp;Nearby yards
            <span class="sos-list-count">{{ nearbyYards.length }}</span>
          </div>
          <div v-if="finding" class="sos-form-noresults"><b-spinner small></b-spinner>&nbsp;Searching&hellip;</div>
          <div v-else-if="resultsShown && !nearbyYards.length" class="sos-form-noresults">No yards within {{ radius }} miles — widen the radius above.</div>
          <div class="sos-yard-scroll" v-if="nearbyYards.length">
            <div v-for="(y, i) in nearbyYards" :key="'l' + i" class="sos-yard-row" @click="focus_yard(y)">
              <span class="sos-yard-num">Y{{ i + 1 }}</span>
              <div class="sos-yard-row-main">
                <div class="sos-yard-company">{{ y.company }}</div>
                <div v-if="y.address" class="sos-yard-addr">{{ y.address }}</div>
              </div>
              <div class="sos-yard-row-side">
                <span class="sos-yard-dist">{{ y.distance }} mi</span>
                <a v-if="y.phone" :href="'tel:' + y.phone" class="sos-yard-phone" @click.stop><i class="fa fa-phone"></i>&nbsp;{{ fmtPhone(y.phone) }}</a>
              </div>
            </div>
          </div>
        </template>
        <!-- yard operators without telematics can join the network as a rescue point -->
        <div class="sos-form-joinline">
          <router-link class="sos-form-joinlink" to="/register/sos">Operate a bus yard? Join the SOS network &rarr;</router-link>
        </div>
      </div>

      <!-- ===================== TENANT (/sos/<slug>) ===================== -->
      <div v-else>
        <div class="sos-form-fields">
          <!-- yard-only provider: no telematics, so the Vehicle ID is just a label and the pin IS
               the location (the locate / identify lookups are skipped entirely) -->
          <label class="sos-form-label">{{ yard_only ? 'Vehicle ID (optional)' : 'Enter your Vehicle ID' }}</label>
          <div class="sos-vehicle-row">
            <b-form-input v-model="vehicle_name" maxlength="120" placeholder="e.g. Bus 1234" autocomplete="off"
                          @keyup.enter="search_vehicle"/>
            <button v-if="!yard_only" type="button" class="sos-pin-btn sos-pin-btn--icon" @click="search_vehicle"
                    :disabled="!vehicle_name || locating_vehicle"
                    title="Search for this Vehicle ID" aria-label="Search for this Vehicle ID">
              <b-spinner v-if="locating_vehicle" small></b-spinner>
              <i v-else class="fa fa-search"></i>
            </button>
          </div>
          <p v-if="yard_only" class="sos-form-hint">{{ display_name }} doesn't track vehicle locations - use your location or tap the map to drop a pin.</p>
          <p v-else-if="locating_vehicle" class="sos-form-hint"><b-spinner small></b-spinner>&nbsp;Locating your vehicle&hellip;</p>
          <p v-else-if="vehicleLocateOk" class="sos-form-hint sos-form-hint--ok"><i class="fa fa-check-circle"></i>&nbsp;{{ vehicleLocateMsg }}</p>
          <p v-else-if="vehicleLocateMsg" class="sos-form-hint sos-form-hint--warn"><i class="fa fa-exclamation-triangle"></i>&nbsp;{{ vehicleLocateMsg }}</p>
        </div>

        <!-- or enter an address; it drops the pin (and a dropped pin fills this in) -->
        <div class="sos-form-fields">
          <label class="sos-form-label">Or enter an address</label>
          <div class="sos-vehicle-row">
            <b-form-input v-model="address" placeholder="Street, city…" autocomplete="off"
                          @keyup.enter="geocode_address" @blur="geocode_address"/>
            <button type="button" class="sos-pin-btn sos-pin-btn--icon" @click="geocode_address" :disabled="geocoding || !address.trim()"
                    title="Find this address on the map" aria-label="Find this address">
              <b-spinner v-if="geocoding" small></b-spinner>
              <i v-else class="fa fa-search"></i>
            </button>
            <button type="button" class="sos-pin-btn sos-pin-btn--icon" @click="locate_me(false)" :disabled="locating"
                    title="Use my current location to drop a pin"
                    :aria-label="yard_only ? 'Drop a pin at my location' : 'Use my location to drop a pin'">
              <b-spinner v-if="locating" small></b-spinner>
              <i v-else class="fa fa-location-arrow"></i>
            </button>
          </div>
        </div>

        <div v-if="geo_error" class="sos-form-geoerr">{{ geo_error }}</div>

        <gmap-map ref="sosMapTenant" :zoom="map_zoom" :center="map_center"
                  :options="themed_map_options"
                  class="sos-form-map sos-form-map--compact" @click="place_pin">
          <gmap-marker v-if="pin_position" :position="pin_position" :draggable="true" @dragend="place_pin"/>
        </gmap-map>

        <div class="sos-form-pinline" v-if="pin_position"><i class="fa fa-map-marker"></i>&nbsp;{{ address || 'Location set on the map' }}<button type="button" class="sos-pin-clear" @click="clear_pin" title="Remove pin" aria-label="Remove pin"><i class="fa fa-times"></i>&nbsp;Remove pin</button></div>
        <div class="sos-form-pinline sos-form-pinline--empty" v-else-if="yard_only">Required &mdash; tap the map or use your location to drop a pin.</div>
        <div class="sos-form-pinline sos-form-pinline--empty" v-else>Optional &mdash; tap the map to drop a pin instead of using your Vehicle ID.</div>
        <p v-if="identifying" class="sos-form-hint"><b-spinner small></b-spinner>&nbsp;Checking for your vehicle at this spot&hellip;</p>
        <p v-else-if="identifyMsg" class="sos-form-hint sos-form-hint--ok"><i class="fa fa-check-circle"></i>&nbsp;{{ identifyMsg }}</p>

        <div class="sos-form-fields">
          <label class="sos-form-label">Your name</label>
          <b-form-input v-model="driver_name" maxlength="120" placeholder="Driver name" autocomplete="off" @input="driverEdited"/>
          <label class="sos-form-label">Your phone number</label>
          <b-form-input v-model="driver_phone" type="tel" maxlength="40" placeholder="Callback number" autocomplete="off" @input="driverEdited"/>
          <label class="sos-form-label">What's going on?</label>
          <b-form-textarea v-model="request_notes" maxlength="500" rows="3" placeholder="Breakdown, accident, passengers on board, etc."/>
          <input v-model="honeypot" class="sos-form-hp" tabindex="-1" autocomplete="off" aria-hidden="true"/>
        </div>

        <div v-if="submit_error" class="sos-form-state sos-form-state--error">{{ submit_error }}</div>
        <div v-else-if="!canSubmitTenant" class="sos-form-pinline sos-form-pinline--empty">{{ yard_only ? 'Drop a pin on the map to send.' : 'Enter a Vehicle ID or drop a pin to send.' }}</div>

        <b-button block size="lg" variant="danger" class="sos-form-submit" :disabled="!canSubmitTenant || submitting || request_sent" @click="submit_tenant">
          <span v-if="submitting"><b-spinner small></b-spinner>&nbsp;Sending&hellip;</span>
          <span v-else>Send SOS to Dispatch</span>
        </b-button>

        <div v-if="request_sent && notified" class="sos-form-sent">
          <span class="sos-form-check-inline">&#10003;</span> Sent to your dispatch team.
          <div v-if="share_token" class="sos-form-refline">
            <router-link class="sos-form-tracklink" :to="'/sos/' + slug + '/' + share_token">Track status &rarr;</router-link>
          </div>
        </div>
        <div v-if="request_sent && !notified" class="sos-form-sent sos-form-sent--warn">
          <span class="sos-form-warn-inline">&#9888;</span> Your SOS was recorded,
          but automatic alerts aren&rsquo;t set up for your company &mdash; <strong>please call your dispatcher directly</strong> to make sure they know.
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { get_sos_form, submit_sos_form, locate_sos_vehicle, identify_sos_at_location, get_sos_nearby, get_sos_all_yards, get_sos_driver_status, add_sos_comment_by_token, geocode_sos_form_address, revgeocode_sos_form_pin } from '../networking/apis'
import { DARK_MAP_STYLES } from '../assets/dark_map_styles'
// GmapCluster is not auto-registered by vue2-google-maps (it loads marker-clusterer-plus
// conditionally); import the component directly and register it locally.
import GmapCluster from 'vue2-google-maps/dist/components/cluster'

// a small round cluster bubble drawn as an inline SVG data-URI, so cluster icons need no external
// image (marker-clusterer-plus otherwise points at a Google-hosted png)
function clusterIcon(size, fill) {
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size + '">'
    + '<circle cx="' + (size / 2) + '" cy="' + (size / 2) + '" r="' + (size / 2 - 2) + '" fill="' + fill + '" opacity="0.92" stroke="#ffffff" stroke-width="2"/></svg>'
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg)
}

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }
const METERS_PER_MILE = 1609.34

// the fleet basemap style (matches vehicle_map.vue / sos_sender_detail.vue / the SOS overlay map)
const FLEET_MAP_STYLE = {
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  styles: [
    { featureType: 'landscape', stylers: [{ color: '#F3F4F9' }] },
    { featureType: 'water', stylers: [{ color: '#C1CDE4' }] },
    { featureType: 'landscape.natural.terrain', stylers: [{ color: '#E0EDCA' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#DDDDDD' }] },
    { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#FFFFFF' }] },
    { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#FEF8D6' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  ],
}

export default {
  name: 'sos_driver_form',
  components: { GmapCluster },
  data() {
    return {
      loading: true,
      loadError: false,
      display_name: '',
      // yard-only provider (no telematics): the pin is required and the Vehicle ID is only a label
      yard_only: false,
      submitting: false,
      finding: false,
      submit_error: null,
      request_sent: false,
      request_id: null,
      share_token: null,
      notified: true,
      statusData: null,
      statusLoading: false,
      statusError: false,
      statusPollTimer: null,
      commentText: '',
      commentBusy: false,
      locating: false,
      geo_error: null,
      pin_position: null,
      address: '',
      geocoding: false,
      map_center: DEFAULT_CENTER,
      map_zoom: 4,
      radius: 60,
      nearbyYards: [],
      allYards: [],
      visibleYards: null,   // yards inside the current map viewport (for "X of Y yards" as you zoom)
      clustering: true,   // group the ~1,000 directory pins into clusters; the viewer can turn it off
      resultsShown: false,
      info: { open: false, position: null, html: '' },
      vehicle_name: '',
      driver_name: '',
      driver_phone: '',
      // true while the driver name/# shown were auto-filled from a vehicle/location lookup (so a
      // later lookup may refresh or clear them); flipped off the moment the user edits either field
      driverFromLookup: false,
      request_notes: '',
      honeypot: '',
      // pre-submit vehicle locate (validate the ID + drop the pin on its live position)
      locating_vehicle: false,
      vehicleLocateMsg: null,
      vehicleLocateOk: false,
      locateTimer: null,
      pinFromVehicle: false,
      // the vehicle id a lookup resolved, sent on submit so the source bus is excluded from its
      // own nearby-rescuer list
      resolvedSamsaraId: null,
      // reverse: identify the vehicle/driver at a dropped pin (when exactly one bus is there)
      identifyTimer: null,
      identifying: false,
      identifyMsg: null,
      suppressLocate: false,
    }
  },
  computed: {
    slug() {
      return this.$route.params.slug
    },
    token() {
      // /sos/<slug>/<token> resolves the SOS by its per-event share token
      return this.$route.params.token || null
    },
    statusMode() {
      return !!this.token
    },
    publicMode() {
      return !this.slug
    },
    radiusMeters() {
      return this.radius * METERS_PER_MILE
    },
    canSubmitTenant() {
      // a yard-only company can't locate a bus by its ID, so the pin is the only way to send
      if (this.yard_only) return !!this.pin_position
      // a driver can send by Vehicle ID OR by dropping a pin - require one or the other
      return !!this.pin_position || !!(this.vehicle_name && this.vehicle_name.trim())
    },
    yardIcon() {
      return this.yardSymbol('#23608f')
    },
    // pins on the public map: the whole directory by default (US-wide overview), narrowing to the
    // nearby results once a pin is dropped
    mapYards() {
      return this.pin_position ? this.nearbyYards : this.allYards
    },
    // cluster only the full-directory overview; once a pin is dropped the nearby results are a small
    // set, so show them as individual, numbered pins (never clustered)
    showClusters() {
      return this.clustering && !this.pin_position
    },
    // the count of yards currently in the map viewport ("X yards"), falling back to the full
    // directory count until the map has reported its bounds
    yardCountLabel() {
      const n = this.visibleYards != null ? this.visibleYards : this.allYards.length
      return n + ' yards'
    },
    // cluster-bubble tiers (small / medium / large) - marker-clusterer-plus picks by cluster size
    clusterStyles() {
      return [
        { url: clusterIcon(36, '#23608f'), width: 36, height: 36, textColor: '#ffffff', textSize: 12 },
        { url: clusterIcon(46, '#1c527a'), width: 46, height: 46, textColor: '#ffffff', textSize: 13 },
        { url: clusterIcon(58, '#173f5f'), width: 58, height: 58, textColor: '#ffffff', textSize: 14 },
      ]
    },
    sourceIcon() {
      return this.pinSymbol('#e0574b')
    },
    statusCenter() {
      const d = this.statusData
      if (!d || d.latitude == null || d.longitude == null) return null
      const lat = Number(d.latitude)
      const lng = Number(d.longitude)
      return (isNaN(lat) || isNaN(lng)) ? null : { lat, lng }
    },
    orderedComments() {
      // oldest first — reads like a conversation
      return [...((this.statusData && this.statusData.comments) || [])]
    },
    dispatchPhone() {
      // pretty-print the dispatch (SOS recipient) number, e.g. "15551230000" -> "(555) 123-0000"
      const raw = (this.statusData && this.statusData.dispatcher_phone) || ''
      let d = raw.replace(/\D/g, '')
      if (d.length === 11 && d[0] === '1') d = d.slice(1)
      if (d.length === 10) return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6)
      return raw
    },
    dispatchPhoneLink() {
      const d = ((this.statusData && this.statusData.dispatcher_phone) || '').replace(/\D/g, '')
      if (!d) return null
      return 'tel:+' + (d.length === 10 ? '1' + d : d)
    },
    escalationPhone() {
      const raw = (this.statusData && this.statusData.escalation_phone) || ''
      let d = raw.replace(/\D/g, '')
      if (d.length === 11 && d[0] === '1') d = d.slice(1)
      if (d.length === 10) return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6)
      return raw
    },
    escalationPhoneLink() {
      const d = ((this.statusData && this.statusData.escalation_phone) || '').replace(/\D/g, '')
      if (!d) return null
      return 'tel:+' + (d.length === 10 ? '1' + d : d)
    },
    themed_map_options() {
      // match the fleet basemap; a signed-in midnight user gets the dark variant (public
      // visitors have no profile, so this resolves to the light fleet style)
      const midnight = (this.$store.state.profile || {}).site_theme === 'midnight'
      return midnight ? { ...FLEET_MAP_STYLE, styles: DARK_MAP_STYLES } : FLEET_MAP_STYLE
    },
  },
  mounted() {
    this.init_for_route()
    // the map containers are flex-grown, so nudge Google Maps to re-fill after the layout settles
    // and whenever the window resizes - otherwise a grown container leaves an unfilled band
    window.addEventListener('resize', this._onWinResize)
    ;[150, 600].forEach(d => setTimeout(this._settleMap, d))
  },
  beforeDestroy() {
    this.stop_status_poll()
    if (this.locateTimer) clearTimeout(this.locateTimer)
    if (this.identifyTimer) clearTimeout(this.identifyTimer)
    window.removeEventListener('resize', this._onWinResize)
    if (this._winResizeTimer) clearTimeout(this._winResizeTimer)
  },
  watch: {
    // the in-app "Track status" link reuses this component (the /sos route family), so
    // re-initialise on any param change rather than relying on a fresh mount
    '$route'() {
      this.stop_status_poll()
      this.request_sent = false
      this.statusData = null
      this.statusError = false
      this.init_for_route()
    },
    vehicle_name() {
      // as the driver types a Vehicle ID, validate it and show its live location on the map so
      // they can confirm (or drag) the pin before sending. Debounced; only in the tenant form.
      if (this.statusMode || this.publicMode) return
      // yard-only: nothing to locate, the ID is just a label on the SOS
      if (this.yard_only) return
      // a value we auto-filled from a pin's reverse lookup must not re-trigger a locate (would
      // move the driver's own pin to the telematics fix and could ping-pong)
      if (this.suppressLocate) { this.suppressLocate = false; return }
      if (this.locateTimer) clearTimeout(this.locateTimer)
      this.vehicleLocateMsg = null
      this.vehicleLocateOk = false
      // a fresh edit invalidates any previously-resolved id until we re-locate
      this.resolvedSamsaraId = null
      this.locateTimer = setTimeout(this.locate_vehicle, 600)
    },
  },
  methods: {
    init_for_route() {
      if (this.statusMode) {
        this.loading = false
        this.load_status()
        // keep "latest status from the dispatcher" fresh without a manual reload
        this.statusPollTimer = setInterval(this.load_status, 30000)
      } else if (this.publicMode) {
        this.loading = false
        // default view: the whole US with every yard as a pin (no auto-locate); dropping a pin or
        // using "my location" then narrows to the nearby list
        this.load_all_yards()
      } else {
        this.load_form()
      }
    },
    stop_status_poll() {
      if (this.statusPollTimer) {
        clearInterval(this.statusPollTimer)
        this.statusPollTimer = null
      }
    },
    async load_status(retries = 0) {
      if (this.statusLoading) return
      this.statusLoading = true
      try {
        const res = await get_sos_driver_status(this.token)
        this.statusData = (res && res.value && res.value.data) || null
        this.statusError = !this.statusData
      } catch (e) {
        // the token lookup is an eventually-consistent scan, so a status view opened the instant the
        // SOS was filed (the driver form routes straight here) can briefly 404 - retry a few times
        // before surfacing an error
        if (retries < 4) {
          this.statusLoading = false
          setTimeout(() => this.load_status(retries + 1), 1200)
          return
        }
        this.statusError = true
      } finally {
        this.statusLoading = false
      }
    },
    statusLabel(s) {
      return ({ open: 'Open', closed: 'Resolved', cancelled: 'Cancelled',
                acknowledged: 'Acknowledged', en_route: 'En route', arrived: 'Arrived',
                declined: "Can't help", unavailable: 'Unavailable', resolved: 'Resolved' })[s] || s
    },
    async add_comment() {
      const text = (this.commentText || '').trim()
      if (!text || this.commentBusy) return
      this.commentBusy = true
      try {
        await add_sos_comment_by_token(this.token, { text, role: 'driver', author: this.statusData && this.statusData.driver_name })
        this.commentText = ''
        await this.load_status()
      } catch (e) {
        // surface nothing intrusive on the public page; the thread just won't update
        this.statusError = false
      } finally {
        this.commentBusy = false
      }
    },
    commentWho(c) {
      const role = c.role === 'dispatcher' ? 'Dispatch' : c.role === 'driver' ? 'Driver' : c.role === 'partner' ? 'Partner' : ''
      return [role, c.author].filter(Boolean).join(' · ') || 'Note'
    },
    fmtTime(iso) {
      if (!iso) return ''
      const dt = new Date(iso)
      if (isNaN(dt.getTime())) return iso
      return dt.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    },
    pinSymbol(color) {
      // square marker, consistent with the SOS overlay/detail maps (not a teardrop)
      return {
        path: 'M -3,-3 L 3,-3 L 3,3 L -3,3 Z',
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 0.6,
        scale: 3.4,
      }
    },
    yardSymbol(color) {
      // a small building/depot silhouette so directory yards read clearly as yards
      return {
        path: 'M -8,0 L 0,-7 L 8,0 L 5.5,0 L 5.5,6 L -5.5,6 L -5.5,0 Z',
        fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 0.8, scale: 1.4,
      }
    },
    // white number centered on a nearby-yard pin, matching the Y# badge on its list row
    markerLabel(text) {
      return { text: String(text), color: '#ffffff', fontSize: '10px', fontWeight: '700' }
    },
    esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
    },
    async load_form() {
      this.loading = true
      this.loadError = false
      try {
        const res = await get_sos_form(this.slug)
        const data = (res && res.value && res.value.data) || null
        if (!data || !data.display_name) {
          this.loadError = true
          return
        }
        this.display_name = data.display_name
        // yard-only is a new hydrate field; an older API simply omits it -> telematics flow
        this.yard_only = data.yard_only === true
        this.locate_me(true)
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    locate_me(silent) {
      if (!navigator.geolocation) {
        if (!silent) this.geo_error = 'Location isn\'t available on this device — tap the map instead.'
        return
      }
      this.geo_error = null
      this.locating = true
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.locating = false
          const p = { lat: pos.coords.latitude, lng: pos.coords.longitude }
          this.pin_position = p
          this.map_center = p
          this.map_zoom = 11
          if (this.publicMode) {
            // reliably recenter the already-initialised map on the user (the reactive :center prop
            // alone doesn't always pan a map that has already rendered at the default center)
            const mapRef = this.$refs.sosMap
            if (mapRef && mapRef.$mapPromise) {
              mapRef.$mapPromise.then((m) => { m.panTo(p); m.setZoom(11) }).catch(() => {})
            }
            this.find_nearby()
          } else {
            this.pinFromVehicle = false
            this.queueIdentify()
          }
        },
        () => {
          this.locating = false
          if (!silent) this.geo_error = 'Couldn\'t get your location — tap the map to drop a pin.'
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      )
    },
    place_pin(ev) {
      this.pin_position = { lat: ev.latLng.lat(), lng: ev.latLng.lng() }
      // a manual pin overrides a vehicle-located one; then try to identify the bus at that spot
      this.pinFromVehicle = false
      if (!this.publicMode && !this.statusMode) this.queueIdentify()
      this.reverse_geocode_pin()   // drop a pin -> fill the address
    },
    clear_pin() {
      // remove a dropped pin and the location it set; cancel any pending "who's here" lookup. Leaves
      // what the driver typed (vehicle ID, name, notes) alone - they can still send by Vehicle ID.
      if (this.identifyTimer) clearTimeout(this.identifyTimer)
      this.pin_position = null
      this.pinFromVehicle = false
      this.identifying = false
      this.identifyMsg = null
      this.address = ''
      this.geo_error = ''
      this.nearbyYards = []
      this.resultsShown = false
      // back to the default full-US, all-yards overview on the public map
      if (this.publicMode) {
        this.map_center = DEFAULT_CENTER
        this.map_zoom = 4
      }
    },
    async geocode_address() {
      // enter an address -> drop a pin on it
      const q = (this.address || '').trim()
      if (!q || this.geocoding || !this.slug) return
      this.geocoding = true
      try {
        const { value: { data = {} } } = await geocode_sos_form_address(this.slug, q)
        if (data.found) {
          this.pin_position = { lat: Number(data.lat), lng: Number(data.lon) }
          this.pinFromVehicle = false
          this.map_center = this.pin_position
          this.map_zoom = 15
          if (data.address) this.address = data.address
          if (!this.publicMode && !this.statusMode) this.queueIdentify()
        } else {
          this.geo_error = "Couldn't find that address — check it or drop a pin on the map."
        }
      } catch (e) {
        this.geo_error = "Couldn't look up that address."
      }
      this.geocoding = false
    },
    async reverse_geocode_pin() {
      if (!this.pin_position || !this.slug) return
      try {
        const { value: { data = {} } } = await revgeocode_sos_form_pin(
          this.slug, this.pin_position.lat.toFixed(6), this.pin_position.lng.toFixed(6))
        if (data.address) this.address = data.address
      } catch (e) { /* best-effort: the pin still works without an address */ }
    },
    search_vehicle() {
      // explicit lookup from the search icon / Enter: cancel any pending debounce and locate the
      // Vehicle ID now, so a driver can force a search or re-search
      if (this.yard_only) return
      if (!this.vehicle_name || !this.vehicle_name.trim() || this.locating_vehicle) return
      if (this.locateTimer) clearTimeout(this.locateTimer)
      this.locate_vehicle()
    },
    async locate_vehicle() {
      const v = (this.vehicle_name || '').trim()
      this.vehicleLocateMsg = null
      this.vehicleLocateOk = false
      if (!v || !this.slug || this.yard_only) return
      this.locating_vehicle = true
      try {
        const { value: { data = {} } } = await locate_sos_vehicle(this.slug, v)
        // ignore a stale response if the driver kept typing
        if (v !== (this.vehicle_name || '').trim()) return
        if (data.found) {
          const lat = Number(data.lat)
          const lng = Number(data.lon)
          if (!isNaN(lat) && !isNaN(lng)) {
            this.pin_position = { lat, lng }
            this.pinFromVehicle = true
            this.map_center = { lat, lng }
            this.map_zoom = 14
            this.vehicleLocateOk = true
            if (data.address) this.address = data.address
            this.vehicleLocateMsg = data.address
              ? ('Located near ' + data.address + " — drag the pin if it's off.")
              : "Vehicle located — drag the pin on the map if it's off."
          }
          this.resolvedSamsaraId = data.samsara_id || null
          this.applyDriver(data.driver)
        } else {
          // only clear a pin we set from a vehicle lookup; never a manually-placed one
          if (this.pinFromVehicle) { this.pin_position = null; this.pinFromVehicle = false }
          this.resolvedSamsaraId = null
          // the vehicle changed to one we can't find -> drop the driver we auto-filled for the old one
          this.applyDriver(null)
          this.vehicleLocateMsg = "We couldn't locate that Vehicle ID — drop a pin on the map instead."
        }
      } catch (e) {
        // network/rate-limit: stay quiet, the driver can still drop a pin or send by ID
        this.vehicleLocateMsg = null
      } finally {
        this.locating_vehicle = false
      }
    },
    applyDriver(driver) {
      // keep the driver name/# in step with the vehicle/location we just resolved: refresh them to
      // the newly-resolved driver, or clear them when none is found. Never touches a value the user
      // typed by hand -- only a field WE auto-filled (driverFromLookup) is updated or cleared here.
      if (!this.driverFromLookup && (this.driver_name || this.driver_phone)) return
      const name = (driver && driver.name) || ''
      const phone = (driver && driver.phone) || ''
      this.driver_name = name
      this.driver_phone = phone
      this.driverFromLookup = !!(name || phone)
    },
    driverEdited() {
      // the user took over the driver fields; stop auto-managing them
      this.driverFromLookup = false
    },
    queueIdentify() {
      if (this.identifyTimer) clearTimeout(this.identifyTimer)
      // yard-only: no vehicle positions to match a pin against
      if (this.yard_only) return
      this.identifyTimer = setTimeout(this.identify_at_pin, 500)
    },
    async identify_at_pin() {
      // reverse lookup: if exactly one bus is at the dropped pin, pre-fill its ID + driver so the
      // driver doesn't have to type them. Never runs for a pin we set from a Vehicle ID lookup.
      if (!this.pin_position || this.pinFromVehicle || !this.slug || this.yard_only) return
      const at = { lat: this.pin_position.lat, lng: this.pin_position.lng }
      // when a Vehicle ID is already resolved, that ID owns the driver — a pin move must not change
      // it. Only a location-only flow (no resolved Vehicle ID) lets the identified bus drive it.
      const hadVehicle = !!this.resolvedSamsaraId
      this.identifying = true
      this.identifyMsg = null
      try {
        const { value: { data = {} } } = await identify_sos_at_location(this.slug, at.lat.toFixed(6), at.lng.toFixed(6))
        // ignore if the pin moved again while we were waiting
        if (!this.pin_position || this.pin_position.lat !== at.lat || this.pin_position.lng !== at.lng) return
        if (data.found) {
          if (!this.vehicle_name && data.vehicle_name) {
            // set the ID without re-triggering the Vehicle ID locate (the pin is already correct)
            this.suppressLocate = true
            this.vehicle_name = data.vehicle_name
            this.resolvedSamsaraId = data.samsara_id || null
          }
          if (!hadVehicle) this.applyDriver(data.driver)
          if (data.vehicle_name) this.identifyMsg = 'Found ' + data.vehicle_name + ' at this location.'
        } else if (!hadVehicle) {
          // pin dropped where no bus can be identified, and no Vehicle ID typed -> clear the driver
          // we auto-filled from a previous location
          this.applyDriver(null)
        }
      } catch (e) {
        this.identifyMsg = null
      } finally {
        this.identifying = false
      }
    },
    place_pin_and_search(ev) {
      this.place_pin(ev)
      this.find_nearby()
    },
    focus_yard(y, setPin = false) {
      const lat = Number(y.lat)
      const lng = Number(y.lon)
      if (isNaN(lat) || isNaN(lng)) return
      this.map_center = { lat, lng }
      this.info = {
        open: true,
        position: { lat, lng },
        html: '<div style="font-size:14px;max-width:240px"><b>' + this.esc(y.company) + '</b>'
          + (y.address ? '<br/>' + this.esc(y.address) : '')
          + (y.phone ? '<br/>' + this.esc(this.fmtPhone(y.phone)) : '') + '</div>',
      }
      // clicking a yard PIN also drops the search pin there and lists what else is nearby, so a
      // driver can pivot the search to that yard's area (the list rows just focus, no re-pin)
      if (setPin && this.publicMode) {
        this.pin_position = { lat, lng }
        this.pinFromVehicle = false
        this.reverse_geocode_pin()
        this.find_nearby()
      }
    },
    _yardAddrKey(y) {
      // Key a yard by its physical location so listings that spell the SAME address differently
      // still collapse - e.g. "24243 Co Rd 7, St Augusta, MN 56301" / "24243 County Road 7, Saint
      // Augusta, Minnesota, 56301" / "24243 Country Road 7 Street Augusta Minnesota 56301" all key
      // on street number 24243 + ZIP 56301. Falls back to the normalized full string when a number
      // or ZIP is missing.
      const a = String(y.address || '')
      const num = (a.match(/\d{1,6}/) || [])[0]                 // leading street number
      const zips = a.match(/\b\d{5}\b/g) || []
      const zip = zips.length ? zips[zips.length - 1] : null    // last 5-digit run = ZIP
      if (num && zip && num !== zip) return 'nz:' + num + '|' + zip
      const norm = a.trim().toLowerCase().replace(/\s+/g, ' ')
      return norm ? 'a:' + norm : null
    },
    _dedupeYards(list) {
      // the directory can carry several rows for one physical yard (association syncs + a customer
      // listing at the same street, often with different spellings). Collapse rows for the same
      // location to a single pin/row, keeping the one WITH a phone number (a stranded driver needs
      // someone to call).
      const at = new Map()
      const out = []
      for (const y of (list || [])) {
        const key = this._yardAddrKey(y)
        if (!key) { out.push(y); continue }   // no address to match on - keep it
        const idx = at.get(key)
        if (idx === undefined) {
          at.set(key, out.length)
          out.push(y)
        } else if (!out[idx].phone && y.phone) {
          out[idx] = y   // upgrade the kept row to the duplicate that has a phone
        }
      }
      return out
    },
    fmtPhone(raw) {
      // pretty-print a US/Canada number as (xxx) xxx-xxxx; leave anything non-standard as typed
      const r = String(raw || '')
      let d = r.replace(/\D/g, '')
      if (d.length === 11 && d[0] === '1') d = d.slice(1)
      if (d.length === 10) return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6)
      return r
    },
    async load_all_yards() {
      // fetch the whole public directory for the default all-pins overview (US-wide)
      try {
        const res = await get_sos_all_yards()
        const data = (res && res.value && res.value.data) || {}
        this.allYards = this._dedupeYards(data.yards || [])
      } catch (e) {
        this.allYards = []
      }
    },
    async find_nearby() {
      if (!this.pin_position || this.finding) return
      this.finding = true
      this.submit_error = null
      try {
        const res = await get_sos_nearby(this.pin_position.lat.toFixed(6), this.pin_position.lng.toFixed(6), this.radius)
        const data = (res && res.value && res.value.data) || {}
        this.nearbyYards = this._dedupeYards(data.yards || [])
        this.resultsShown = true
        // pan/zoom so the whole selected radius is in view (runs on every pin drop and radius change)
        this.$nextTick(this.fit_to_radius)
      } catch (e) {
        this.submit_error = 'Something went wrong searching. Try again.'
      } finally {
        this.finding = false
      }
    },
    fit_to_radius() {
      if (!this.$refs.sosMap || !this.pin_position) return
      this.$refs.sosMap.$mapPromise.then((map) => {
        const g = window.google && window.google.maps
        if (!g) return
        // Zoom so the search circle's DIAMETER roughly fills the smaller map dimension - a tight
        // fit with only a small buffer (or a slight edge crop), which is much closer in than the
        // full-circle fitBounds. Zoom is integer-only on this raster map, so we pick the nearest
        // level to the ideal fill; the circle is a search-area indicator, so a little crop is fine.
        const el = this.$refs.sosMap.$el
        const minDim = Math.min(el.offsetWidth || 0, el.offsetHeight || 0) || 320
        const metersPerPxZ0 = 156543.03392 * Math.cos(this.pin_position.lat * Math.PI / 180)
        // floor (not round) the ideal zoom so the circle diameter never EXCEEDS the smaller map
        // dimension - rounding up would chop the circle on a narrow (mobile) viewport. This is the
        // tightest integer zoom that still shows the whole circle.
        const zoom = Math.log2(minDim * metersPerPxZ0 / (2 * this.radiusMeters))
        map.setCenter(this.pin_position)
        map.setZoom(Math.max(1, Math.min(20, Math.floor(zoom))))
      }).catch(() => {})
    },
    _settleMap() {
      // re-fill whichever map is on screen after its flex-grown container changed size (initial
      // layout or a window resize); recenter so the target stays framed after the resize
      const ref = this.$refs.sosMap || this.$refs.sosMapTenant
      if (!ref || !ref.$mapPromise) return
      ref.$mapPromise.then((map) => {
        const g = window.google && window.google.maps
        if (!g) return
        g.event.trigger(map, 'resize')
        if (this.map_center) map.setCenter(this.map_center)
        // once: watch the public map's viewport so the count reads "X of Y yards" as you zoom/pan
        if (this.publicMode && !this._boundsWatch) {
          this._boundsWatch = true
          map.addListener('idle', () => this._updateVisibleCount(map))
          this._updateVisibleCount(map)
        }
      }).catch(() => {})
    },
    _updateVisibleCount(map) {
      const g = window.google && window.google.maps
      const bounds = map && map.getBounds && map.getBounds()
      if (!g || !bounds) { this.visibleYards = null; return }
      let n = 0
      for (const y of this.mapYards) {
        const lat = Number(y.lat), lng = Number(y.lon)
        if (!isNaN(lat) && !isNaN(lng) && bounds.contains(new g.LatLng(lat, lng))) n++
      }
      this.visibleYards = n
    },
    _onWinResize() {
      if (this._winResizeTimer) clearTimeout(this._winResizeTimer)
      this._winResizeTimer = setTimeout(this._settleMap, 150)
    },
    async submit_tenant() {
      if (!this.canSubmitTenant || this.submitting) return
      this.submitting = true
      this.submit_error = null
      try {
        const body = {
          vehicle_name: this.vehicle_name,
          samsara_id: this.resolvedSamsaraId,
          driver_name: this.driver_name,
          driver_phone: this.driver_phone,
          request_notes: this.request_notes,
          max_miles_away: this.radius,
          _honeypot: this.honeypot,
        }
        // only send coordinates when a pin was actually dropped; otherwise the backend locates the
        // bus from its Vehicle ID via telematics
        if (this.pin_position) {
          body.lat = this.pin_position.lat.toFixed(6)
          body.lon = this.pin_position.lng.toFixed(6)
        }
        const res = await submit_sos_form(this.slug, body)
        const data = (res && res.value && res.value.data) || {}
        this.request_id = data.request_id || null
        // the per-event share token the driver can use to track status at /sos/<slug>/<token>
        this.share_token = data.share_token || null
        // backend reports whether an email/SMS alert actually went out; if not, tell the driver
        // honestly to call dispatch rather than falsely claiming it was delivered
        this.notified = data.notified !== false
        this.request_sent = true
        // load the driver status page for the SOS just filed (it shows status, dispatch/escalation
        // contacts and the comment thread, and any notification-failure warning). The $route watcher
        // re-initialises this component into status mode. Fall back to the inline sent-confirmation
        // only when no token came back.
        if (this.share_token) {
          this.$router.push('/sos/' + this.slug + '/' + this.share_token)
          return
        }
      } catch (e) {
        // most common failure is a Vehicle ID we couldn't match/locate - point the driver at the
        // two ways to send rather than a dead-end generic error (yard-only has only the pin)
        this.submit_error = this.yard_only
          ? 'Drop a pin on the map, then try again - or call your dispatcher directly.'
          : "We couldn't send your SOS. Check your Vehicle ID or drop a pin on the map, then try again — or call your dispatcher directly."
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.sos-form-bg {
  min-height: 100vh;
  background: #f4f5f7;
  padding: 12px;
  box-sizing: border-box;
}
.sos-form-card {
  max-width: 520px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 16px;
}
/* Public page: pin the layout to the viewport so only the yard list scrolls -
   everything above it (header, radius, map, list heading) stays in view. */
.sos-form-bg--fixed {
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
}
.sos-form-card--fill {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  padding: 12px;
  /* the public map page is wider than the driver form - it's a map + yard list, not a form.
     On phones the viewport caps it; on wider screens it uses the room. */
  max-width: 900px;
}
.sos-public {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.sos-public > * {
  flex: 0 0 auto;
}
.sos-public .sos-yard-scroll {
  /* basis 0 so the map:list split is set purely by their flex-grow ratio */
  flex: 1 1 0;
  min-height: 0;
  max-height: none;
}
/* the map GROWS to fill the card's height so there's never dead space below it (before a pin is
   dropped it fills the whole area); once a pin is dropped the map and the nearby-yard list share
   the room (both grow). Short landscape viewports pin it small again in the media query below. */
.sos-public .sos-form-map--compact {
  flex: 1 1 0;
  min-height: 220px;
  height: auto;
}
/* once a pin is set the nearby list appears; give the map a bigger share (grow 1.5 vs the list's
   1 -> ~60/40, i.e. ~20% taller than an even split) so more of the map stays visible */
.sos-public .sos-form-map--compact.sos-form-map--pinned {
  flex-grow: 1.5;
}
.sos-form-header {
  border-bottom: 1px solid #eee;
  padding-bottom: 12px;
  margin-bottom: 12px;
}
.sos-form-header--slim {
  padding-bottom: 6px;
  margin-bottom: 6px;
}
.sos-form-header--oneline {
  padding-bottom: 8px;
  margin-bottom: 10px;
}
.sos-form-oneline {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 0;
  min-width: 0;
  white-space: nowrap;
}
.sos-form-brand {
  flex: 0 0 auto;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 20px;
  font-weight: 800;
  color: #c0392b;
}
.sos-form-oneline-co {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: right;
  font-size: 18px;
  font-weight: 700;
  color: #1c2733;
}
.sos-form-oneline-sub {
  flex: 0 0 auto;
  font-size: 18px;
  font-weight: 700;
  color: #1c2733;
}
.sos-form-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 20px;
  font-weight: 800;
  color: #c0392b;
}
.sos-form-eyebrow--h1 {
  margin: 0;
  line-height: 1.1;
}
.sos-form-lead {
  font-size: 14px;
  color: #47566a;
  margin-bottom: 10px;
}
.sos-tenant-lead-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.sos-form-lead--inline {
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 700;
  color: #1c2733;
}
.sos-form-state {
  padding: 16px;
  text-align: center;
  color: #47566a;
}
.sos-form-state--error {
  color: #ae3f2e;
  background: #f6e4e0;
  border-radius: 8px;
  margin: 10px 0;
}
.sos-ctrl-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.sos-ctrl-radius {
  flex: 1 1 auto;
  min-width: 0;
}
.sos-ctrl-label {
  font-size: 13px;
  font-weight: 600;
  color: #47566a;
}
.sos-ctrl-count {
  flex: 1 1 auto;
  font-size: 13px;
  font-weight: 600;
  color: #47566a;
}
/* "Cluster" text sits to the LEFT of the switch */
.sos-cluster-wrap {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.sos-cluster-lead {
  font-size: 13px;
  color: #47566a;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}
.sos-cluster-toggle {
  flex: 0 0 auto;
  font-size: 13px;
  color: #47566a;
  white-space: nowrap;
}
.sos-form-slider {
  width: 100%;
  margin-top: 2px;
}
.sos-icon-btn {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 1px solid #cdd6de;
  background: #fff;
  border-radius: 8px;
  color: #23608f;
  font-size: 16px;
  cursor: pointer;
}
.sos-icon-btn:hover {
  border-color: #23608f;
}
.sos-icon-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.sos-form-maprow {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.sos-form-geoerr {
  font-size: 13px;
  color: #a4650e;
  margin: 4px 0;
}
.sos-form-map {
  width: 100%;
  height: 320px;
  border-radius: 10px;
  overflow: hidden;
}
.sos-form-map--compact {
  height: 190px;
  min-height: 150px;
}
.sos-form-pinline {
  font-size: 13px;
  font-weight: 600;
  color: #1c2733;
  margin: 6px 0 4px;
  font-variant-numeric: tabular-nums;
}
.sos-form-pinline--empty {
  font-weight: 400;
  color: #8295a7;
}
.sos-pin-clear {
  margin-left: 10px;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #c0392b;
  background: transparent;
  border: 1px solid #e0b4ad;
  border-radius: 5px;
  cursor: pointer;
}
.sos-pin-clear:hover {
  background: #fbe9e4;
}
.sos-form-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 8px 0 4px;
}
.sos-form-label {
  display: block;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #47566a;
  margin: 8px 0 2px;
}
.sos-form-hint {
  font-size: 12.5px;
  color: #8295a7;
  margin: 4px 0 2px;
  text-align: left;
}
.sos-form-hint--ok { color: #2f7d4f; font-weight: 600; }
.sos-form-hint--warn { color: #9a5b00; font-weight: 600; }
/* public page footer: the yard-only self-registration link (sits under the scrolling yard list) */
.sos-form-joinline {
  padding-top: 8px;
  margin-top: 6px;
  border-top: 1px solid #eee;
  text-align: center;
  font-size: 12.5px;
}
.sos-form-joinlink {
  color: #23608f;
  font-weight: 600;
  text-decoration: none;
}
.sos-form-joinlink:hover {
  text-decoration: underline;
}
.sos-vehicle-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}
.sos-vehicle-row > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}
.sos-pin-btn {
  flex: 0 0 auto;
  white-space: nowrap;
  border: 1px solid #cdd6de;
  background: #fff;
  border-radius: 8px;
  color: #23608f;
  font-size: 13px;
  font-weight: 600;
  padding: 0 12px;
  cursor: pointer;
}
.sos-pin-btn:hover { border-color: #23608f; }
.sos-pin-btn:disabled { opacity: 0.6; cursor: default; }
/* icon-only variant (the Vehicle ID search button) — a compact square tap target */
.sos-pin-btn--icon {
  min-width: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.sos-form-hp {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.sos-form-submit {
  font-weight: 700;
  margin-top: 12px;
}
.sos-form-sent {
  margin-top: 10px;
  background: #e3f0e7;
  border-radius: 8px;
  padding: 10px 12px;
  color: #2f7d4f;
  font-weight: 600;
  font-size: 14px;
}
.sos-form-check-inline {
  font-weight: 700;
}
.sos-form-sent--warn {
  background: #fdf0e3;
  color: #9a5b00;
}
.sos-form-warn-inline {
  font-weight: 700;
}
.sos-form-refline {
  margin-top: 6px;
  font-weight: 500;
}
.sos-form-refcode {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
}
.sos-form-tracklink {
  margin-left: 8px;
  font-weight: 700;
  color: #23608f;
  text-decoration: none;
  white-space: nowrap;
}
.sos-form-tracklink:hover { text-decoration: underline; }

/* ---- driver status view (/sos/<code>) ---- */
.sos-status-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.sos-status-refbox {
  display: flex;
  flex-direction: column;
}
.sos-status-reflabel {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8295a7;
}
.sos-status-ref {
  font-size: 34px;
  font-weight: 800;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.08em;
  color: #1c2733;
}
.sos-status-chip {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 4px 12px;
  border-radius: 999px;
  background: #f6e4e0;
  color: #ae3f2e;
  white-space: nowrap;
}
.sos-status-chip--closed, .sos-status-chip--resolved,
.sos-status-chip--arrived, .sos-status-chip--acknowledged, .sos-status-chip--en_route { background: #e3f0e7; color: #2f7d4f; }
.sos-status-chip--cancelled { background: #eef1f3; color: #6e7e8f; }
.sos-status-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  background: #fff;
  border: 1px solid #e6e9ec;
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 10px;
}
.sos-status-facts > div {
  display: flex;
  gap: 6px;
  align-items: baseline;
}
.sos-status-wide { flex-basis: 100%; }
.sos-status-k {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8295a7;
  white-space: nowrap;
}
.sos-status-v { font-size: 14px; color: #1c2733; }
.sos-status-tel { color: #23608f; font-weight: 600; text-decoration: none; white-space: nowrap; }
.sos-status-tel:hover { text-decoration: underline; }
.sos-status-comments {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sos-status-comment {
  padding: 7px 10px;
  border-radius: 8px;
  background: #f6f8fa;
  border-left: 3px solid #c9d2da;
}
.sos-status-comment--driver { border-left-color: #23608f; }
.sos-status-comment--dispatcher { border-left-color: #2f7d4f; }
.sos-status-comment--system { border-left-color: #b8c0c9; background: #f7f8fa; }
.sos-status-comment--system .sos-status-combody { color: #55636f; font-style: italic; }
.sos-status-comment { text-align: left; }
.sos-status-comhead {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 8px;
}
.sos-status-comwho { font-weight: 700; font-size: 13px; color: #1c2733; }
.sos-status-comtime { font-size: 12px; color: #8295a7; }
.sos-status-combody { color: #2b3642; font-size: 13.5px; margin-top: 2px; white-space: pre-wrap; word-break: break-word; text-align: left; }
.sos-status-comadd {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.sos-status-comsend { align-self: flex-end; }
.sos-status-foot {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}
.sos-status-autonote { font-size: 12px; color: #8295a7; }
.sos-list-head {
  font-size: 15px;
  font-weight: 700;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
}
.sos-list-head--info {
  background: #eef1f3;
  color: #47566a;
}
.sos-list-count {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
}
.sos-form-noresults {
  color: #8295a7;
  font-size: 14px;
  margin: 8px 2px;
}
.sos-yard-scroll {
  margin-top: 8px;
  max-height: 42vh;
  overflow-y: auto;
  border: 1px solid #eef1f3;
  border-radius: 8px;
}
.sos-yard-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
}
.sos-yard-row:last-child {
  border-bottom: none;
}
.sos-yard-row:hover {
  background: #f7f9fb;
}
.sos-yard-num {
  flex: 0 0 auto;
  min-width: 26px;
  padding: 2px 6px;
  border-radius: 6px;
  background: #eaf1f8;
  color: #23608f;
  font-weight: 800;
  font-size: 12px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.sos-yard-row-main {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}
.sos-yard-company {
  font-weight: 700;
  color: #1c2733;
}
.sos-yard-addr {
  font-size: 13px;
  color: #6e7e8f;
}
.sos-yard-row-side {
  flex: 0 0 auto;
  text-align: right;
}
.sos-yard-dist {
  display: block;
  font-size: 13px;
  color: #6e7e8f;
  font-variant-numeric: tabular-nums;
}
.sos-yard-phone {
  display: inline-block;
  margin-top: 4px;
  font-weight: 600;
  font-size: 13px;
  color: #23608f;
  text-decoration: none;
  white-space: nowrap;
}
.sos-yard-phone:hover {
  text-decoration: underline;
}

/* ---- responsive: public SOS map page ---- */
/* short viewports (landscape phones, small laptops): shrink the map and tighten the header so
   the yard list keeps usable room in the viewport-locked layout */
@media (max-height: 560px) {
  /* pin the map small (don't let it grow) so the yard list keeps usable room when a pin is dropped */
  .sos-public .sos-form-map--compact,
  .sos-public .sos-form-map--compact.sos-form-map--pinned { flex: 0 0 auto; height: clamp(110px, 22dvh, 170px); min-height: 0; }
  .sos-form-header--slim { padding-bottom: 4px; margin-bottom: 4px; }
  .sos-form-eyebrow--h1 { font-size: 17px; }
}
/* narrow phones: tighten side padding so the card and rows never clip or scroll sideways */
@media (max-width: 380px) {
  .sos-form-bg { padding: 8px; }
  .sos-form-card--fill { padding: 10px; }
  .sos-yard-row { padding: 8px 10px; gap: 8px; }
}
</style>
